import { useApp } from "@/application/composables/app";
import { AppIdentifier } from "@/application/enums";
import { useMetaStore } from "@/application/stores/meta";
import { useLocalStorage, useTitle } from "@vueuse/core";
import { useI18n } from "@/application/plugins/i18n";
import { computed } from "vue";
import { useTheme } from "vuetify";
import { hexToHSL } from "@/application/utils/convertHEXtoHSL";

export const useAppTheme = () => {
  const { appData, appIdentifier } = useApp();
  const metaStore = useMetaStore();
  const i18n = useI18n();
  const theme = useTheme();

  // local storage value
  const title = useLocalStorage("pageTitle", appData.title);

  // html title watcher
  useTitle(title);

  const text = i18n.t("labels.pageTitleAdmin");
  const centralWebsitePageTitle = i18n.t("labels.pageTitleCentralWebsite");

  const currentPrimaryColor = computed(
    (): string => theme.themes.value.light.colors["primary"],
  );

  const setPageTitle = () => {
    if (appIdentifier === AppIdentifier.CentralWebsite) {
      title.value = `${appData.title} | ${centralWebsitePageTitle}`;
      return;
    }

    if (appIdentifier === AppIdentifier.CentralAdmin) {
      title.value = `${appData.title} | ${text}`;
      return;
    }

    if (appIdentifier === AppIdentifier.TenantAdmin) {
      if (!metaStore.meta?.name) return;
      title.value = `${metaStore.meta.name} | ${text}`;
      return;
    }

    if (appIdentifier === AppIdentifier.TenantWebsite) {
      title.value = metaStore.meta?.name || appData.title;
      return;
    }
  };

  // calculate color variants based on primary color
  const adjustPrimaryColorThroughApplication = (
    color: string = currentPrimaryColor.value,
  ) => {
    const hsl =
      hexToHSL(color) ||
      (hexToHSL(currentPrimaryColor.value) as {
        h: number;
        s: number;
        l: number;
      });

    theme.themes.value.light.colors["primary"] = color;
    theme.themes.value.dark.colors["primary"] = color;

    theme.themes.value.light.colors["accent"] =
      `hsl(${hsl.h}, ${hsl.s}, ${hsl.l - 5}%)`;
    theme.themes.value.dark.colors["accent"] =
      `hsl(${hsl.h}, ${hsl.s}, ${hsl.l - 5}%)`;

    theme.themes.value.light.colors["stepper-surface"] =
      `hsl(${hsl.h}, ${hsl.s}, 86%)`;
    theme.themes.value.dark.colors["stepper-surface"] =
      `hsl(${hsl.h}, ${hsl.s}, 86%)`;

    theme.themes.value.light.colors["secondary"] =
      `hsl(${hsl.h}, ${hsl.s / 4}%, 97%)`;
    theme.themes.value.dark.colors["secondary"] =
      `hsl(${hsl.h}, ${hsl.s / 4}%, 97%)`;
  };

  return {
    setPageTitle,
    adjustPrimaryColorThroughApplication,
  };
};
